import { apiClient, apiDownload } from "./apiClient";

export type PurchaseInvoiceVendor = "canon" | "idid";

export type CanonMapping = { id: string; serial: string; customer: string; checkSerial: boolean; checkEstimate: boolean; estimateOnlyZeroPages: boolean; active: boolean; reason: string };
export type CanonMappingSettings = { version: string; mappings: CanonMapping[]; updatedAt: string | null; updatedBy: string | null };

export type FindingGuidanceContent = { recipient: string; steps: string[]; emailTemplate: string };
export type FindingGuidanceSettings = {
  version: string;
  guidance: Record<PurchaseInvoiceVendor, Record<string, FindingGuidanceContent>>;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type UploadedAnalysisFile = {
  name: string;
  contentBase64: string;
};

export type PurchaseInvoiceAnalysisPayload = {
  vendor: PurchaseInvoiceVendor;
  invoicePdf: File;
  billingExcel: File;
};

export type IdidContractMapping = {
  id: string;
  invoiceContract: string;
  invoiceCustomer: string;
  t360Contract: string;
  t360Customer: string;
};

export type IdidProductMapping = {
  id: string;
  skProduct: string;
  productName: string;
  included: boolean;
};

export type IdidMappings = {
  contractMappings: IdidContractMapping[];
  productMappings: IdidProductMapping[];
};

export type AnalysisFinding = {
  id: string;
  category: string;
  severity: "info" | "warning" | "error";
  title: string;
  message: string;
  context: Record<string, unknown>;
};

export type VendorDocumentation = {
  vendor: PurchaseInvoiceVendor;
  content: string;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type PurchaseInvoiceAnalysis = {
  id: string;
  vendor: PurchaseInvoiceVendor;
  status: "ok" | "warnings" | "errors";
  createdAt: string;
  metadata: Record<string, string | null>;
  summary: Record<string, number>;
  findings: AnalysisFinding[];
  rows: Array<Record<string, unknown>>;
};

export const purchaseInvoicesApi = {
  getCanonMappings: () => apiClient<CanonMappingSettings>("/purchase-invoices/canon/mappings"),
  saveCanonMapping: (version: string, saveMapping: CanonMapping) => apiClient<CanonMappingSettings>("/purchase-invoices/canon/mappings", { method: "PATCH", body: JSON.stringify({ version, saveMapping }) }),
  deleteCanonMapping: (version: string, deleteMapping: string) => apiClient<CanonMappingSettings>("/purchase-invoices/canon/mappings", { method: "PATCH", body: JSON.stringify({ version, deleteMapping }) }),
  getGuidance: () => apiClient<FindingGuidanceSettings>("/purchase-invoices/guidance"),
  saveGuidance: (vendor: PurchaseInvoiceVendor, category: string, version: string, content: FindingGuidanceContent) =>
    apiClient<FindingGuidanceSettings>(`/purchase-invoices/${vendor}/guidance/${category}`, { method: "PATCH", body: JSON.stringify({ version, ...content }) }),
  getDocumentation: (vendor: PurchaseInvoiceVendor) =>
    apiClient<VendorDocumentation>(`/purchase-invoices/${vendor}/documentation`),
  saveDocumentation: (vendor: PurchaseInvoiceVendor, content: string) =>
    apiClient<VendorDocumentation>(`/purchase-invoices/${vendor}/documentation`, {
      method: "PATCH",
      body: JSON.stringify({ content })
    }),
  listMappings: () => apiClient<IdidMappings>("/purchase-invoices/idid/mappings"),
  createContractMapping: (payload: Omit<IdidContractMapping, "id">) =>
    apiClient<IdidContractMapping>("/purchase-invoices/idid/contract-mappings", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateContractMapping: (id: string, payload: Omit<IdidContractMapping, "id">) =>
    apiClient<IdidContractMapping>(`/purchase-invoices/idid/contract-mappings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  deleteContractMapping: (id: string) =>
    apiClient<void>(`/purchase-invoices/idid/contract-mappings/${id}`, { method: "DELETE" }),
  createProductMapping: (payload: Omit<IdidProductMapping, "id">) =>
    apiClient<IdidProductMapping>("/purchase-invoices/idid/product-mappings", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  updateProductMapping: (id: string, payload: Omit<IdidProductMapping, "id">) =>
    apiClient<IdidProductMapping>(`/purchase-invoices/idid/product-mappings/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload)
    }),
  deleteProductMapping: (id: string) =>
    apiClient<void>(`/purchase-invoices/idid/product-mappings/${id}`, { method: "DELETE" }),
  exportAnalysis: (analysis: PurchaseInvoiceAnalysis) =>
    apiDownload("/purchase-invoices/export", {
      method: "POST",
      body: JSON.stringify(analysis)
    }),
  analyze: (payload: PurchaseInvoiceAnalysisPayload) =>
    {
      const formData = new FormData();
      formData.append("vendor", payload.vendor);
      formData.append("invoicePdf", payload.invoicePdf);
      formData.append("billingExcel", payload.billingExcel);
      return apiClient<PurchaseInvoiceAnalysis>("/purchase-invoices/analyze", {
        method: "POST",
        body: formData
      });
    }
};
